import { useEffect } from "react";
import Reveal from "@/components/site/Reveal";
import SectionLabel from "@/components/site/SectionLabel";
import Testimonials from "@/components/sections/Testimonials";
import CTASection from "@/components/sections/CTASection";

const TRANSFORMATIONS = [
    {
        before: "Hadn't been on a date in 14 months.",
        after: "On 8 first-dates by Day 70.",
        label: "From Stuck to Selecting",
    },
    {
        before: "Crippling approach anxiety in public.",
        after: "Initiates conversations daily without thinking.",
        label: "From Frozen to Fluid",
    },
    {
        before: "Constantly chasing, getting ghosted, doubting himself.",
        after: "Calm, grounded, women pursue him — and he qualifies them.",
        label: "From Chasing to Chosen",
    },
    {
        before: "Numb routine, dating life on autopilot.",
        after: "Disciplined lifestyle, sharper standards, deeper relationships.",
        label: "From Drifting to Direction",
    },
];

const METRICS = [
    { n: "90", l: "Days of structured transformation" },
    { n: "3", l: "Pillars trained in parallel" },
    { n: "1:1", l: "Direct coaching every week" },
    { n: "0", l: "Pickup tricks, scripts, or games" },
];

export default function Results() {
    useEffect(() => {
        document.title = "Results & Transformation | 90 Day Alpha";
    }, []);

    return (
        <div data-testid="results-page" className="bg-[#020617]">
            <section className="relative pt-40 pb-16 md:pt-48 md:pb-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <SectionLabel testId="results-label">
                            The Transformation
                        </SectionLabel>
                        <h1 className="mt-6 font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.98] max-w-4xl">
                            The kind of man{" "}
                            <span className="text-[#D4AF37]">
                                90 days
                            </span>{" "}
                            actually builds.
                        </h1>
                        <p className="mt-8 text-white/75 leading-relaxed text-base sm:text-lg max-w-2xl">
                            We don’t promise specific women or guaranteed
                            outcomes. We do promise this: the man who finishes
                            90 Day Alpha is not the man who started it. Below
                            are the patterns we see in clients who do the work.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-16 md:py-20 bg-[#020617] border-y border-[#142142]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#142142] border border-[#142142]">
                    {METRICS.map((m, i) => (
                        <Reveal key={i} delay={i * 0.06}>
                            <div
                                data-testid={`metric-${i + 1}`}
                                className="bg-[#020617] p-8 text-center h-full"
                            >
                                <p className="font-display font-black text-[#D4AF37] text-5xl md:text-6xl leading-none tracking-tighter">
                                    {m.n}
                                </p>
                                <p className="mt-3 text-xs uppercase tracking-[0.2em] font-semibold text-white/70">
                                    {m.l}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#060c1e]">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <h2 className="font-display font-black text-white text-4xl sm:text-5xl tracking-tight leading-tight max-w-3xl">
                            Before <span className="text-[#8B0000]">/</span>{" "}
                            <span className="text-[#D4AF37]">After</span>
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {TRANSFORMATIONS.map((t, i) => (
                            <Reveal key={i} delay={i * 0.08}>
                                <div
                                    data-testid={`transformation-${i + 1}`}
                                    className="bg-[#020617] border border-[#142142] p-8 md:p-10 hover:border-[#1E90FF]/50 transition-all duration-500"
                                >
                                    <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D4AF37]">
                                        {t.label}
                                    </p>
                                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <div className="border-l-2 border-[#8B0000] pl-4">
                                            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#8B0000]">
                                                Before
                                            </p>
                                            <p className="mt-2 text-white/70 leading-relaxed">
                                                {t.before}
                                            </p>
                                        </div>
                                        <div className="border-l-2 border-[#D4AF37] pl-4">
                                            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#D4AF37]">
                                                After
                                            </p>
                                            <p className="mt-2 text-white/90 leading-relaxed">
                                                {t.after}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <Testimonials />
            <CTASection />
        </div>
    );
}
